import {useCallback, useMemo, useState} from 'react';

import {FILE_UPLOAD_ACCEPTED_FORMATS} from './file-upload.constants';
import {UploadErrorReason} from './file-upload.types';
import {isAcceptedFileType, parseAcceptedFormats} from './file-upload.utils';
import {FileUploadZone} from './FileUploadZone';
import {PendingFileList} from './PendingFileList';
import {UploadedDocumentList} from './UploadedDocumentList';
import {FieldError} from '../shadcn/field';

import type {PendingFile, UploadedFile} from './file-upload.types';

export interface FileUploadProps {
  documents: UploadedFile[];
  maxFiles?: number;
  isUploading?: boolean;
  /**
   * Current upload progress 0–100 — shown on all pending items.
   * Note: when multiple files are uploading concurrently, all items share
   * this single value. Per-file progress tracking requires per-file callbacks.
   */
  progress?: number;
  acceptedFormats?: string;
  /** Files exceeding this size (in bytes) are rejected before upload. */
  maxFileSizeBytes?: number;
  clickLabel?: string;
  dragLabel?: string;
  maxSizeLabel?: string;
  /** Inline error shown when a selected file exceeds maxFileSizeBytes. */
  fileTooLargeLabel?: string;
  /** Inline error shown when a selected file doesn't match acceptedFormats. */
  invalidFileTypeLabel?: string;
  /** Caller-owned error. Hidden while an upload is in flight, and overridden by a file-rejection message. */
  error?: string;
  uploadingLabel?: string;
  deleteLabel?: string;
  downloadLabel?: string;
  viewLabel?: string;
  onUpload: (file: File) => Promise<void>;
  onDelete: (fileId: string) => Promise<void>;
  onDownload?: (fileId: string) => Promise<string>;
  onView?: (fileId: string) => Promise<void>;
  /** Called whenever the file-rejection state changes — e.g. to surface an error indicator elsewhere (a tab, a section header). Never fires for `error`, which the caller already owns. */
  onErrorChange?: (hasError: boolean) => void;
  /** When true, hides the upload dropzone and delete affordance — documents become view/download only. */
  readOnly?: boolean;
}

export function FileUpload({
  documents,
  maxFiles,
  isUploading = false,
  progress,
  acceptedFormats,
  maxFileSizeBytes,
  clickLabel,
  dragLabel,
  maxSizeLabel,
  fileTooLargeLabel,
  invalidFileTypeLabel,
  error,
  uploadingLabel,
  onUpload,
  onDelete,
  onDownload,
  onView,
  onErrorChange,
  deleteLabel,
  downloadLabel,
  viewLabel,
  readOnly = false
}: FileUploadProps) {
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [uploadError, setUploadError] = useState<UploadErrorReason | null>(null);

  const totalCount = documents.length + pendingFiles.length;
  const canAddMore = maxFiles === undefined || totalCount < maxFiles;
  const acceptedEntries = useMemo(
    () => parseAcceptedFormats(acceptedFormats ?? FILE_UPLOAD_ACCEPTED_FORMATS),
    [acceptedFormats]
  );

  const handleUpload = useCallback(
    async (item: PendingFile) => {
      try {
        await onUpload(item.file);
      } finally {
        setPendingFiles(prev => prev.filter(p => p.id !== item.id));
      }
    },
    [onUpload]
  );

  const handleFilesSelect = useCallback(
    (files: File[]) => {
      let hasInvalidType = false;
      let hasTooLarge = false;
      const validFiles = files.filter(file => {
        if (!isAcceptedFileType(file, acceptedEntries)) {
          hasInvalidType = true;
          return false;
        }
        if (maxFileSizeBytes !== undefined && file.size > maxFileSizeBytes) {
          hasTooLarge = true;
          return false;
        }
        return true;
      });

      const error = hasInvalidType
        ? UploadErrorReason.INVALID_TYPE
        : hasTooLarge
          ? UploadErrorReason.TOO_LARGE
          : null;
      setUploadError(error);
      onErrorChange?.(error !== null);

      const filesToAdd =
        maxFiles !== undefined ? validFiles.slice(0, maxFiles - totalCount) : validFiles;

      const newItems: PendingFile[] = filesToAdd.map(file => ({
        id: crypto.randomUUID(),
        file
      }));
      setPendingFiles(prev => [...prev, ...newItems]);

      // Auto-upload each file
      newItems.forEach(item => {
        void handleUpload(item);
      });
    },
    [acceptedEntries, maxFiles, maxFileSizeBytes, totalCount, handleUpload, onErrorChange]
  );

  const handleRemovePending = useCallback(
    (id: string) => {
      setPendingFiles(prev => prev.filter(p => p.id !== id));
      setUploadError(null);
      onErrorChange?.(false);
    },
    [onErrorChange]
  );

  // An upload in flight is the user already resolving the external error — don't nag mid-fix.
  const externalError = isUploading || pendingFiles.length > 0 ? undefined : error;

  const errorLabel =
    uploadError === UploadErrorReason.TOO_LARGE
      ? fileTooLargeLabel
      : uploadError === UploadErrorReason.INVALID_TYPE
        ? invalidFileTypeLabel
        : externalError;

  return (
    <div data-slot="file-upload" className="flex w-full flex-col gap-4">
      <div className="flex w-full flex-col gap-1.5">
        {/* Drop zone — hidden once the file limit is reached or in read-only mode */}
        {!readOnly && canAddMore && (
          <FileUploadZone
            onFilesSelect={handleFilesSelect}
            acceptedFormats={acceptedFormats}
            clickLabel={clickLabel}
            dragLabel={dragLabel}
            maxSizeLabel={maxSizeLabel}
            multiple={maxFiles === undefined || maxFiles > 1}
            disabled={isUploading}
            hasError={uploadError !== null || !!externalError}
          />
        )}

        {errorLabel && <FieldError className="whitespace-normal">{errorLabel}</FieldError>}
      </div>

      <PendingFileList
        pendingFiles={pendingFiles}
        progress={progress}
        uploadingLabel={uploadingLabel}
        isUploading={isUploading}
        onRemove={handleRemovePending}
      />
      <UploadedDocumentList
        documents={documents}
        isLoading={isUploading}
        onDelete={readOnly ? undefined : onDelete}
        onDownload={onDownload}
        onView={onView}
        deleteLabel={deleteLabel}
        downloadLabel={downloadLabel}
        viewLabel={viewLabel}
      />
    </div>
  );
}
